// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/campaigns.test.tsx
import { Badge, Flex } from "@radix-ui/themes";
import cx from "classnames";

const MAX_VISIBLE = 3;

export type OrgLabelChipsProps = {
  /** "campaigns" | "chats" */
  testIdPrefix: string;
  entityId: string;
  tags?: string[];
  /** Keep chips on one row next to a title (project cards, list rows). */
  inline?: boolean;
};

/** First-seen unique slugs, case-insensitive. */
export function unionOrgTagSlugs(
  ...groups: (string[] | undefined)[]
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const group of groups) {
    for (const raw of group ?? []) {
      const tag = raw.trim();
      if (!tag) continue;
      const key = tag.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(tag);
    }
  }
  return out;
}

/**
 * Read-only tag chips for launcher list/grid rows.
 * Caps visible chips and shows +N overflow.
 */
export function OrgLabelChips({
  testIdPrefix,
  entityId,
  tags = [],
  inline = false,
}: OrgLabelChipsProps) {
  if (tags.length === 0) return null;

  const visibleTags = tags.slice(0, MAX_VISIBLE);
  const overflow = Math.max(0, tags.length - MAX_VISIBLE);

  return (
    <Flex
      align="center"
      gap="1"
      wrap={inline ? "nowrap" : "wrap"}
      className={cx(inline ? "min-w-0 max-w-full overflow-x-auto" : "mt-1")}
      data-testid={`${testIdPrefix}-row-labels-${entityId}`}
      aria-label="Tags"
      onClick={(e) => e.stopPropagation()}
    >
      {visibleTags.map((slug) => (
        <Badge
          key={`t-${slug}`}
          color="orange"
          variant="soft"
          size="1"
          data-testid={`${testIdPrefix}-row-tag-${entityId}-${slug}`}
        >
          {slug}
        </Badge>
      ))}
      {overflow > 0 ? (
        <Badge
          color="gray"
          variant="soft"
          size="1"
          data-testid={`${testIdPrefix}-row-labels-overflow-${entityId}`}
        >
          +{overflow}
        </Badge>
      ) : null}
    </Flex>
  );
}
